import React, { useReducer, useRef } from "react";
import Select, { SelectInstance } from "react-select";

import type { ProductCategoryModel } from "@admin/domain/productCategories/models/ProductCategoryModel";
import type { ActiveComponentModel as Base } from "@admin/domain/products/models/edit/ProductSpecificationsModel";
import convertToLanguageMap, { description, title } from "@admin/domain/languages/helpers";
import Input from "@admin/components/Input";
import MultiLanguages from "@admin/components/MultiLanguage";

type ActiveComponentModel = Base & { title: string; description: string };

type ActiveComponentsReducerProps = {
    action: `delete` | `add`;
    id: ActiveComponentModel[`id`];
};

type Option = {
    value: ActiveComponentModel[`id`];
    label: ActiveComponentModel[`title`];
};

type Props = {
    productCategory: ProductCategoryModel | null;
};

function reducer(
    selected: Array<ActiveComponentModel[`id`]>,
    { action, id }: ActiveComponentsReducerProps,
): Array<ActiveComponentModel[`id`]> {
    switch (action) {
        case `add`:
            return [...selected, id];

        case `delete`:
            return selected.filter((item) => item !== id);

        default:
            throw Error(`Unknown action: ` + action);
    }
}

export default function ActiveComponentsForSpecialCategory({ productCategory }: Props) {
    const allActiveComponents = (window.CMS.activeComponents || []).map((value): ActiveComponentModel => {
        const textsMap = convertToLanguageMap(value.texts);

        return {
            ...value,
            textsMap,
            title: title(value.texts),
            description: description(value.texts),
        };
    });

    const [activeComponents, dispatch] = useReducer(reducer, productCategory?.activeComponents || []);
    const selectRef = useRef<SelectInstance<Option> | null>(null);

    return (
        <div className="mb-5">
            <label className="form-label" htmlFor="input_active_component">
                Активные компоненты
            </label>

            <div className="mb-5">
                <Select
                    ref={selectRef}
                    placeholder="Выберите"
                    options={allActiveComponents
                        .filter((item) => !activeComponents.includes(item.id))
                        .map(
                            (item): Option => ({
                                value: item.id,
                                label: `${item.title || item.id}`,
                            }),
                        )
                        .sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1))}
                    onChange={(item, { action }) => {
                        if (action === `select-option` && item?.value) {
                            dispatch({ action: `add`, id: item.value });
                        }
                    }}
                />
            </div>

            {activeComponents.map((id: number, index: number) => {
                const activeComponent = allActiveComponents.find((item) => item.id === id);

                if (activeComponent) {
                    return (
                        <div key={activeComponent.id} className="card border mb-5 position-relative">
                            <input type="hidden" name={`active_components[${index}][id]`} value={activeComponent.id} />

                            <div className="card-body">
                                <MultiLanguages
                                    languageElement={(language) => (
                                        <Input
                                            defaultValue={activeComponent.title}
                                            label="Название"
                                            name={`active_components[${index}][texts][${language}][title]`}
                                        />
                                    )}
                                />

                                <MultiLanguages
                                    languageElement={(language) => (
                                        <Input
                                            defaultValue={activeComponent.description}
                                            label="Описание"
                                            name={`active_components[${index}][texts][${language}][description]`}
                                        />
                                    )}
                                />
                            </div>

                            <div className="position-absolute top-0 end-0">
                                <button
                                    type="button"
                                    title="Удалить"
                                    className="btn btn-icon btn-icon-danger"
                                    onClick={() => {
                                        dispatch({
                                            action: `delete`,
                                            id: activeComponent.id,
                                        });
                                    }}
                                >
                                    <i className="fa fa-trash" />
                                </button>
                            </div>
                        </div>
                    );
                }

                return null;
            })}
        </div>
    );
}
